import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import * as agentApi from '@/lib/agents/api';
import type {
  CrewProcess,
  AgentRole,
  CrewTask,
  CreateCrewInput,
  LLMProvider,
} from '@/lib/agents/types';
import { CREW_PROCESS_INFO, LLM_PROVIDER_INFO } from '@/lib/agents/types';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Users,
  ListTodo,
  ArrowRight,
  GitBranch,
  GitMerge,
  MessageCircle,
  Loader2,
  GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

export const Route = createFileRoute('/_dashboard/crews/create')({
  component: CreateCrewPage,
});

// Icon mapping for process types
const PROCESS_ICONS: Record<CrewProcess, React.ElementType> = {
  sequential: ArrowRight,
  hierarchical: GitBranch,
  parallel: GitMerge,
  consensual: MessageCircle,
};

function CreateCrewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [processType, setProcessType] = useState<CrewProcess>('sequential');
  const [llmProvider, setLlmProvider] = useState<LLMProvider>('anthropic');
  const [managerAgentId, setManagerAgentId] = useState<string | null>(null);

  // Members and tasks
  const [members, setMembers] = useState<AgentRole[]>([]);
  const [tasks, setTasks] = useState<CrewTask[]>([]);

  // Add member form
  const [newMember, setNewMember] = useState<Partial<AgentRole>>({
    name: '',
    description: '',
    goal: '',
    capabilities: [],
  });
  const [newCapability, setNewCapability] = useState('');

  // Add task form
  const [newTask, setNewTask] = useState<Partial<CrewTask>>({
    description: '',
    expectedOutput: '',
    assignedAgent: '',
  });

  const addMember = () => {
    if (!newMember.name || !newMember.description || !newMember.goal) {
      toast.error('Please fill in all required fields');
      return;
    }

    const member: AgentRole = {
      id: uuidv4(),
      name: newMember.name,
      description: newMember.description,
      goal: newMember.goal,
      backstory: newMember.backstory,
      capabilities: newMember.capabilities || [],
    };

    setMembers([...members, member]);
    setNewMember({ name: '', description: '', goal: '', capabilities: [] });
  };

  const removeMember = (id: string) => {
    setMembers(members.filter((m) => m.id !== id));
    // Also remove from tasks
    setTasks(tasks.filter((t) => t.assignedAgent !== id));
  };

  const addCapability = () => {
    if (!newCapability.trim()) return;
    setNewMember({
      ...newMember,
      capabilities: [...(newMember.capabilities || []), newCapability.trim()],
    });
    setNewCapability('');
  };

  const removeCapability = (cap: string) => {
    setNewMember({
      ...newMember,
      capabilities: (newMember.capabilities || []).filter((c) => c !== cap),
    });
  };

  const addTask = () => {
    if (!newTask.description || !newTask.expectedOutput || !newTask.assignedAgent) {
      toast.error('Please fill in all required fields');
      return;
    }

    const task: CrewTask = {
      id: uuidv4(),
      description: newTask.description,
      expectedOutput: newTask.expectedOutput,
      assignedAgent: newTask.assignedAgent,
      dependencies: newTask.dependencies || [],
    };

    setTasks([...tasks, task]);
    setNewTask({ description: '', expectedOutput: '', assignedAgent: '' });
  };

  const removeTask = (id: string) => {
    setTasks(tasks.filter((t) => t.id !== id));
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Crew name is required');
      return;
    }

    if (members.length === 0) {
      toast.error('Crew must have at least one member');
      return;
    }

    if (tasks.length === 0) {
      toast.error('Crew must have at least one task');
      return;
    }

    if (processType === 'hierarchical' && !managerAgentId) {
      toast.error('Hierarchical process requires a manager agent');
      return;
    }

    setLoading(true);
    try {
      const input: CreateCrewInput = {
        name: name.trim(),
        description: description.trim() || undefined,
        processType,
        managerAgentId: managerAgentId || undefined,
        members,
        tasks,
        config: {
          llmProvider,
        },
      };

      const response = await agentApi.createCrew(input);
      toast.success('Crew created successfully');
      navigate({ to: `/crews/${response.data.id}` });
    } catch (error) {
      toast.error((error as Error).message || 'Failed to create crew');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container max-w-4xl py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate({ to: '/crews' })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Create Crew</h1>
          <p className="text-muted-foreground">Build a team of agents to work together</p>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {[1, 2, 3, 4].map((s) => (
          <div
            key={s}
            className={`h-2 w-16 rounded-full ${s <= step ? 'bg-primary' : 'bg-muted'}`}
          />
        ))}
      </div>

      {/* Step 1: Basic Info */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Crew Details</CardTitle>
            <CardDescription>Name and configure your crew</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Crew Name *</Label>
              <Input
                id="name"
                placeholder="Research Team"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what this crew does..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Process Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {(Object.entries(CREW_PROCESS_INFO) as [CrewProcess, typeof CREW_PROCESS_INFO[CrewProcess]][]).map(
                  ([type, info]) => {
                    const Icon = PROCESS_ICONS[type];
                    const isSelected = processType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setProcessType(type)}
                        className={`p-3 rounded-lg border text-left transition-colors ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <Icon className="h-5 w-5 mb-2" />
                        <div className="font-medium text-sm">{info.label}</div>
                        <div className="text-xs text-muted-foreground">{info.description}</div>
                      </button>
                    );
                  }
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>LLM Provider</Label>
              <Select value={llmProvider} onValueChange={(v) => setLlmProvider(v as LLMProvider)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(LLM_PROVIDER_INFO).map(([provider, info]) => (
                    <SelectItem key={provider} value={provider}>
                      {info.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} disabled={!name.trim()}>
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Add Members */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Members
            </CardTitle>
            <CardDescription>Add agent roles to your crew</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Existing members */}
            {members.length > 0 && (
              <div className="space-y-2">
                <Label>Current Members ({members.length})</Label>
                <div className="space-y-2">
                  {members.map((member) => (
                    <div
                      key={member.id}
                      className="flex items-center justify-between p-3 rounded-lg border bg-muted/50"
                    >
                      <div>
                        <div className="font-medium">{member.name}</div>
                        <div className="text-sm text-muted-foreground">{member.goal}</div>
                        {member.capabilities.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {member.capabilities.map((cap) => (
                              <Badge key={cap} variant="outline" className="text-xs">
                                {cap}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeMember(member.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add member form */}
            <div className="space-y-4 p-4 rounded-lg border border-dashed">
              <h4 className="font-medium">Add New Member</h4>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    placeholder="Researcher"
                    value={newMember.name || ''}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Goal *</Label>
                  <Input
                    placeholder="Find accurate information"
                    value={newMember.goal || ''}
                    onChange={(e) => setNewMember({ ...newMember, goal: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  placeholder="Expert researcher who finds and analyzes information..."
                  value={newMember.description || ''}
                  onChange={(e) => setNewMember({ ...newMember, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Backstory (Optional)</Label>
                <Textarea
                  placeholder="Background story for this agent role..."
                  value={newMember.backstory || ''}
                  onChange={(e) => setNewMember({ ...newMember, backstory: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Capabilities</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add capability..."
                    value={newCapability}
                    onChange={(e) => setNewCapability(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCapability())}
                  />
                  <Button type="button" variant="outline" onClick={addCapability}>
                    Add
                  </Button>
                </div>
                {(newMember.capabilities?.length ?? 0) > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {newMember.capabilities?.map((cap) => (
                      <Badge
                        key={cap}
                        variant="secondary"
                        className="cursor-pointer"
                        onClick={() => removeCapability(cap)}
                      >
                        {cap} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <Button onClick={addMember} className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Add Member
              </Button>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button onClick={() => setStep(3)} disabled={members.length === 0}>
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Add Tasks */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ListTodo className="h-5 w-5" />
              Tasks
            </CardTitle>
            <CardDescription>Define tasks for your crew to complete</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Existing tasks */}
            {tasks.length > 0 && (
              <div className="space-y-2">
                <Label>Current Tasks ({tasks.length})</Label>
                <div className="space-y-2">
                  {tasks.map((task, index) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-2 p-3 rounded-lg border bg-muted/50"
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="font-medium text-sm">Task {index + 1}</div>
                        <div className="text-sm text-muted-foreground line-clamp-1">
                          {task.description}
                        </div>
                        <Badge variant="outline" className="text-xs mt-1">
                          Assigned: {members.find((m) => m.id === task.assignedAgent)?.name}
                        </Badge>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeTask(task.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add task form */}
            <div className="space-y-4 p-4 rounded-lg border border-dashed">
              <h4 className="font-medium">Add New Task</h4>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  placeholder="Research the topic and gather relevant information..."
                  value={newTask.description || ''}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Expected Output *</Label>
                <Textarea
                  placeholder="A comprehensive report with citations..."
                  value={newTask.expectedOutput || ''}
                  onChange={(e) => setNewTask({ ...newTask, expectedOutput: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Assigned Agent *</Label>
                <Select
                  value={newTask.assignedAgent || ''}
                  onValueChange={(v) => setNewTask({ ...newTask, assignedAgent: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button onClick={addTask} className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Add Task
              </Button>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button onClick={() => setStep(4)} disabled={tasks.length === 0}>
                Next
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review & Create */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Review & Create</CardTitle>
            <CardDescription>Confirm your crew configuration</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Name</Label>
                <p className="font-medium">{name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Process</Label>
                <p className="font-medium">{CREW_PROCESS_INFO[processType].label}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">LLM Provider</Label>
                <p className="font-medium capitalize">{llmProvider}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Members</Label>
                <p className="font-medium">{members.length} agent(s)</p>
              </div>
            </div>

            {description && (
              <div>
                <Label className="text-muted-foreground">Description</Label>
                <p>{description}</p>
              </div>
            )}

            <div>
              <Label className="text-muted-foreground">Team Members</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {members.map((member) => (
                  <Badge key={member.id} variant="secondary">
                    {member.name}
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-muted-foreground">Tasks ({tasks.length})</Label>
              <div className="space-y-1 mt-1">
                {tasks.map((task, index) => (
                  <div key={task.id} className="text-sm">
                    <span className="font-medium">{index + 1}.</span>{' '}
                    {task.description.slice(0, 60)}
                    {task.description.length > 60 && '...'}
                  </div>
                ))}
              </div>
            </div>

            {processType === 'hierarchical' && (
              <div className="space-y-2">
                <Label>Manager Agent *</Label>
                <Select
                  value={managerAgentId || ''}
                  onValueChange={(v) => setManagerAgentId(v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(3)}>
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Crew
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
